import React, { useState } from 'react';
import { View, ScrollView, Pressable } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Icon } from '../../../components/Icon';
import { Illustration } from '../../../components/Illustration';
import { ICONS } from '../../../components/Icon/registry';
import { ILLUSTRATIONS } from '../../../components/Illustration/registry';

import tw from '../../../styles/tailwind';
import Text from '../../../components/Text';

const TINT_OPTIONS: Array<{ label: string; value: string | undefined }> = [
  { label: 'none',   value: undefined },
  { label: 'red',    value: '#ef4444' },
  { label: 'blue',   value: '#3b82f6' },
  { label: 'green',  value: '#16a34a' },
  { label: 'purple', value: '#510C76' },
  { label: 'white',  value: '#ffffff' },
  { label: 'black',  value: '#000000' },
];

const BG_OPTIONS: Array<{ label: string; tw: string }> = [
  { label: 'white', tw: 'bg-white' },
  { label: 'gray',  tw: 'bg-gray-200' },
  { label: 'dark',  tw: 'bg-gray-800' },
];

export default function IconShowcaseScreen() {
  const [tint, setTint] = useState<string | undefined>(undefined);
  const [bgTw, setBgTw] = useState<string>('bg-gray-100');

  return (
    <View style={tw`flex-1 bg-white`}>
      <SafeAreaView style={tw`flex-1`} edges={['top']}>
        <ScrollView contentContainerStyle={tw`p-4`}>
          <Text style={tw`text-xl font-bold mb-3`}>Icon Showcase</Text>

          {/* Tint picker */}
          <View style={tw`flex-row flex-wrap items-center mb-2`}>
            <Text style={tw`mr-2 text-gray-600 text-xs`}>Tint:</Text>
            {TINT_OPTIONS.map(opt => {
              const selected = tint === opt.value;
              return (
                <Pressable
                  key={opt.label}
                  onPress={() => setTint(opt.value)}
                  style={[
                    tw`mr-2 mb-2 px-2 py-1 rounded border`,
                    selected ? tw`border-black bg-gray-200` : tw`border-gray-300`,
                  ]}
                >
                  <Text style={tw`text-[11px]`}>{opt.label}</Text>
                </Pressable>
              );
            })}
          </View>

          {/* Background picker */}
          <View style={tw`flex-row flex-wrap items-center mb-4`}>
            <Text style={tw`mr-2 text-gray-600 text-xs`}>BG:</Text>
            {BG_OPTIONS.map(opt => {
              const cellBg = `bg-${opt.label === 'white' ? 'white' : opt.label === 'gray' ? 'gray-200' : 'gray-800'}`;
              const selected = bgTw === cellBg;
              return (
                <Pressable
                  key={opt.label}
                  onPress={() => setBgTw(cellBg)}
                  style={[
                    tw`mr-2 mb-2 px-2 py-1 rounded border`,
                    selected ? tw`border-black bg-gray-200` : tw`border-gray-300`,
                  ]}
                >
                  <Text style={tw`text-[11px]`}>{opt.label}</Text>
                </Pressable>
              );
            })}
          </View>

          {/* Icons section */}
          <Text style={tw`text-lg font-bold mt-2 mb-2`}>
            Icons ({Object.keys(ICONS).length})
          </Text>
          {Object.entries(ICONS).map(([name, variants]) => (
            <View key={name} style={tw`mb-3 border-b border-gray-200 pb-2`}>
              <Text style={tw`text-gray-700 mb-2 font-bold text-xs`}>{name}</Text>
              <View style={tw`flex-row flex-wrap`}>
                {Object.keys(variants).map(variant => (
                  <View key={variant} style={tw`items-center mr-3 mb-2`}>
                    <View
                      style={tw.style(
                        'w-14 h-14 items-center justify-center rounded',
                        bgTw,
                      )}
                    >
                      <Icon
                        name={name as any}
                        variant={variant as any}
                        size={32}
                        color={tint}
                      />
                    </View>
                    <Text style={tw`text-[10px] text-gray-500 mt-1`}>{variant}</Text>
                  </View>
                ))}
              </View>
            </View>
          ))}

          {/* Illustrations section */}
          <Text style={tw`text-lg font-bold mt-4 mb-2`}>
            Illustrations ({Object.keys(ILLUSTRATIONS).length})
          </Text>
          {Object.entries(ILLUSTRATIONS).map(([name, variants]) => (
            <View key={name} style={tw`mb-3 border-b border-gray-200 pb-2`}>
              <Text style={tw`text-gray-700 mb-2 font-bold text-xs`}>{name}</Text>
              <View style={tw`flex-row flex-wrap`}>
                {Object.keys(variants).map(variant => (
                  <View key={variant} style={tw`items-center mr-3 mb-2`}>
                    <View
                      style={tw.style(
                        'w-20 h-20 items-center justify-center rounded',
                        bgTw,
                      )}
                    >
                      <Illustration
                        name={name as any}
                        variant={variant as any}
                        width={56}
                        height={56}
                        color={tint}
                      />
                    </View>
                    <Text style={tw`text-[10px] text-gray-500 mt-1`}>{variant}</Text>
                  </View>
                ))}
              </View>
            </View>
          ))}
        </ScrollView>
      </SafeAreaView>
    </View>
  );
}
